import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

function InsightTaxReform() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-btacc-blue z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="text-2xl font-montserrat font-bold text-white">
                BT<span className="text-btacc-gold">ACC</span>
              </h1>
              <p className="text-xs text-btacc-light font-inter -mt-1">Brazil Tax Advisory & Compliance Center</p>
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-btacc-blue hover:text-btacc-gold transition mb-8">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>

          {/* Article Header */}
          <div className="mb-8">
            <div className="text-sm text-btacc-secondary mb-4">BTACC Insights</div>
            <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-btacc-blue mb-6">
              Understanding Brazil's Tax Reform: IVA Dual System
            </h1>
            <p className="text-xl text-btacc-graphite font-inter">
              Brazil's new dual VAT system (CBS and IBS) represents the most significant tax reform in decades. Here's what U.S. companies need to know.
            </p>
          </div>

          {/* Featured Image */}
          <img 
            src="/images/btacc-insight.png"
            alt="BTACC - Brazil Tax Advisory & Compliance Center"
            className="w-full h-96 object-cover rounded-lg mb-12"
          />

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">What is the IVA Dual System?</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              The IVA Dual system introduces two separate value-added taxes that will replace Brazil's complex web of federal, state, and municipal taxes. This historic reform aims to simplify Brazil's notoriously complicated tax system and improve the business environment.
            </p>

            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">CBS - Contribution on Goods and Services</h3>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              The CBS (Contribuição sobre Bens e Serviços) is a federal tax that will replace PIS, COFINS, and IPI. This federal component is expected to have a rate between 12-13% and will be administered by the Federal Revenue Service.
            </p>

            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">IBS - Tax on Goods and Services</h3>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              The IBS (Imposto sobre Bens e Serviços) is a subnational tax that will replace ICMS and ISS. Managed by states and municipalities, it represents a significant shift in how regional taxes are collected and distributed.
            </p>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Impact on U.S. Companies</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              For American companies operating in or exporting to Brazil, this reform brings both opportunities and challenges:
            </p>

            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2"><strong>Simplified Compliance:</strong> Fewer taxes means less administrative burden</li>
              <li className="mb-2"><strong>Transparent Rates:</strong> Clearer tax structure makes planning easier</li>
              <li className="mb-2"><strong>Transition Period:</strong> Implementation will occur gradually through 2033</li>
              <li className="mb-2"><strong>Supply Chain Impact:</strong> Tax credits and calculations will change</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Timeline and Implementation</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              The reform will be implemented in phases:
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2"><strong>2026:</strong> Testing phase begins</li>
              <li className="mb-2"><strong>2027:</strong> CBS becomes effective at 1%</li>
              <li className="mb-2"><strong>2029-2032:</strong> Gradual transition period</li>
              <li className="mb-2"><strong>2033:</strong> Full implementation</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">How BTACC Can Help</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              Our team of 120+ Brazilian tax specialists is actively monitoring this reform and helping U.S. companies prepare for the changes. We offer:
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2">Impact assessments for your specific business</li>
              <li className="mb-2">Transition planning and compliance strategies</li>
              <li className="mb-2">Ongoing monitoring of regulatory changes</li>
              <li className="mb-2">Training for your finance and operations teams</li>
            </ul>

            <div className="bg-btacc-light p-6 rounded-lg mt-8 mb-8">
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-3">Need Expert Guidance?</h3>
              <p className="text-btacc-graphite font-inter mb-4">
                The tax reform is complex and will have significant implications for your Brazilian operations. Contact BTACC today to ensure your company is prepared.
              </p>
              <button onClick={handleContactClick} className="inline-block bg-btacc-blue text-white px-6 py-3 rounded-md font-montserrat font-semibold hover:bg-btacc-secondary transition cursor-pointer">
                Schedule a Consultation
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-btacc-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <h2 className="text-2xl font-montserrat font-bold">
              BT<span className="text-btacc-gold">ACC</span>
            </h2>
          </Link>
          <div className="text-sm text-btacc-light mb-4">
            <p>Brazil Tax Advisory & Compliance Center</p>
            <p>320 Decker Drive, Irving, Texas 75062</p>
          </div>
          <p className="text-sm text-btacc-light">
            © {new Date().getFullYear()} BTACC — All rights reserved. / Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default InsightTaxReform;